/**
 * A2A Client Configuration for PriChineseDaily
 * 
 * Initializes the A2A client for communicating with the A2AChineseAI backend server.
 * This client handles all A2A protocol communication including:
 * - Capability discovery
 * - Task execution
 * - UX negotiation
 * - Message signing and authentication
 */

import { A2AClient } from '../shared/a2a-client.js';

// A2A Backend Server URLs
const LOCALHOST_URL = 'http://localhost:8787';
const PRODUCTION_URL = 'https://a2achineseai-server.appsokay.net';

// Get initial URL from localStorage or environment variable
// Default to PRODUCTION (Cloudflare) for deployed apps
const getInitialUrl = () => {
  // Detect if we're on Cloudflare/production
  const isProduction = typeof window !== 'undefined' && 
                       window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.includes('localhost');
  
  // If on production, force production URL (ignore localStorage)
  if (isProduction) {
    // Clear any localhost preference
    if (typeof window !== 'undefined') {
      localStorage.setItem('a2a_use_localhost', 'false');
    }
    return PRODUCTION_URL;
  }
  
  // For localhost, check localStorage first (set by toggle)
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('a2a_use_localhost');
    if (saved === 'true') {
      return LOCALHOST_URL;
    }
    if (saved === 'false') {
      return PRODUCTION_URL;
    }
  }
  
  // Fallback to environment variable or production (default to production for Cloudflare)
  return process.env.REACT_APP_A2A_BACKEND_URL || PRODUCTION_URL;
};

const REMOTE_AGENT_URL = getInitialUrl();

// Client Agent ID - Unique for PriChineseDaily
const AGENT_ID = 'prichinesedaily-client-001';

// A2A Secret Key (optional, for message signing)
// Load from environment variable if available
const SECRET_KEY = process.env.REACT_APP_A2A_SECRET_KEY || null;

// Log configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('[A2A Client Config]', {
    backendURL: REMOTE_AGENT_URL,
    agentID: AGENT_ID,
    hasSecretKey: !!SECRET_KEY,
    isLocalhost: REMOTE_AGENT_URL.includes('localhost'),
    note: 'Use the A2A Server toggle (bottom left) to switch between localhost and production'
  });
}

/**
 * Initialize A2A Client
 */
const chineseClient = new A2AClient(REMOTE_AGENT_URL, AGENT_ID, {
  secretKey: SECRET_KEY,
  timeout: 30000,       // 30 second timeout
  maxRetries: 3,        // Retry failed requests up to 3 times
  enableLogging: process.env.NODE_ENV === 'development', // Enable logging in development
});

/**
 * Update A2A server URL dynamically
 * Called by the A2A Server toggle component
 */
export const updateA2AServerUrl = (newUrl) => {
  chineseClient.updateRemoteUrl(newUrl);
  // Clear cached capabilities to force rediscovery
  chineseClient.clearCache();
  capabilitiesInitialized = false;
  initializationPromise = null;
  console.log(`[A2A Client] Server URL updated to: ${newUrl}`);
  // Re-initialize capabilities with new URL
  initializeCapabilities().catch(error => {
    console.warn('[A2A Client] Failed to reinitialize with new URL:', error.message);
  });
};

// Expose to window for toggle component
if (typeof window !== 'undefined') {
  window.updateA2AServerUrl = updateA2AServerUrl;
}

/**
 * Auto-initialize capabilities on module load
 * This ensures capabilities are discovered when the app starts
 */
let capabilitiesInitialized = false;
let initializationPromise = null;

export const initializeCapabilities = async () => {
  if (capabilitiesInitialized) {
    return chineseClient.getCapabilities();
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      console.log('[A2A Client] Discovering capabilities from backend...');
      const capabilities = await chineseClient.discoverCapabilities();
      capabilitiesInitialized = true;
      console.log(`[A2A Client] ✓ Capability discovery successful: Found ${capabilities.length} capabilities from ${chineseClient.remoteUrl}`);
      console.log(`[A2A Client] Discovered ${capabilities.length} capabilities:`, 
        capabilities.map(c => c.name).join(', ')
      );
      return capabilities;
    } catch (error) {
      // Check if this is a connection error - these are expected and handled by fallback
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      
      if (!isConnectionError) {
        // Only log non-connection errors
        console.error('[A2A Client] Failed to discover capabilities:', error);
      }
      throw error;
    }
  })();

  return initializationPromise;
};

// Auto-initialize capabilities when the module is loaded
if (typeof window !== 'undefined') {
  // Only initialize if we're in a browser environment
  setTimeout(() => {
    initializeCapabilities().catch(error => {
      // Only log if it's not a connection error (expected when localhost server is not running)
      // Connection errors are expected and will be handled by fallback mechanisms
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      
      if (!isConnectionError || process.env.NODE_ENV === 'development') {
        // Log non-connection errors or all errors in development
        console.warn('[A2A Client] Auto-initialization failed (will use fallback):', error.message);
      }
      // Silently handle connection errors in production - fallback will be used
    });
  }, 1000); // Delay 1 second to avoid blocking initial page load
}

/**
 * Helper functions for common operations
 */

/**
 * Check if A2A backend is available
 * @returns {Promise<boolean>}
 */
export const checkBackendAvailability = async () => {
  try {
    const capabilities = await chineseClient.discoverCapabilities();
    return capabilities && capabilities.length > 0;
  } catch (error) {
    console.error('[A2A Client] Backend not available:', error);
    return false;
  }
};

/**
 * Get capability information by name
 * @param {string} capabilityName 
 * @returns {Promise<Object|null>}
 */
export const getCapabilityInfo = async (capabilityName) => {
  return await chineseClient.getCapability(capabilityName);
};

/**
 * Check if a specific capability is available
 * @param {string} capabilityName 
 * @returns {Promise<boolean>}
 */
export const hasCapability = async (capabilityName) => {
  return await chineseClient.hasCapability(capabilityName);
};

/**
 * Get all available capabilities
 * @returns {Array|null}
 */
export const getCapabilities = () => {
  return chineseClient.getCapabilities();
};

/**
 * Execute a capability/task
 * @param {string} capabilityName 
 * @param {Object} parameters 
 * @param {Object} options 
 * @returns {Promise<any>}
 */
export const executeTask = async (capabilityName, parameters = {}, options = {}) => {
  return await chineseClient.executeTask(capabilityName, parameters, options);
};

/**
 * Negotiate UX format based on user context
 * @param {Object} context - User context (device, screen size, bandwidth, etc.)
 * @returns {Promise<Object>}
 */
export const negotiateUX = async (context = null) => {
  const userContext = context || chineseClient.detectContext();
  return await chineseClient.negotiateUX(userContext);
};

/**
 * Detect current user context
 * @returns {Object}
 */
export const detectContext = () => {
  return chineseClient.detectContext();
};

/**
 * Get rate limit information
 * @returns {Object}
 */
export const getRateLimitInfo = () => {
  return chineseClient.getRateLimitInfo();
};

/**
 * Clear cached capabilities (force refresh)
 */
export const clearCache = () => {
  chineseClient.clearCache();
  capabilitiesInitialized = false;
  initializationPromise = null;
};

/**
 * Cleanup client (call when unmounting app)
 */
export const cleanup = () => {
  chineseClient.cleanup();
};

/**
 * Export the client instance as default
 */
export default chineseClient;

